import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getHuscleosLeads, getHuscleosStats, updateHuscleosLead, addHuscleosNote } from '../../lib/api';
import api from '../../lib/api';
import DemoSwitcher from './DemoSwitcher';
import MRRStatsBar from './MRRStatsBar';
import {
  RefreshCw, Loader2, Copy, ExternalLink, ChevronDown, ChevronUp,
  Mail, Phone, MapPin, Dumbbell, Heart, Flame, Clock,
  MessageSquare, Send, ArrowRightLeft, Users, Eye, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

// Light-theme pipeline palette — black/white/grey with the HuscleOS red accent for "Converted"
const PIPELINE = [
  { key: 'New',         label: 'New Leads',   color: '#0F0F0F', dot: '#0F0F0F', chipBg: '#F3F4F6', chipFg: '#0F0F0F' },
  { key: 'Demo Sent',   label: 'Demo Sent',   color: '#6B7280', dot: '#6B7280', chipBg: '#F3F4F6', chipFg: '#6B7280' },
  { key: 'Engaged',     label: 'Engaged',     color: '#374151', dot: '#374151', chipBg: '#F3F4F6', chipFg: '#374151' },
  { key: 'Call Booked', label: 'Call Booked', color: '#1F2937', dot: '#1F2937', chipBg: '#F3F4F6', chipFg: '#1F2937' },
  { key: 'Converted',   label: 'Converted',   color: '#FF2E00', dot: '#FF2E00', chipBg: '#FFEDE8', chipFg: '#FF2E00' },
  { key: 'Lost',        label: 'Lost',        color: '#9CA3AF', dot: '#D1D5DB', chipBg: '#F9FAFB', chipFg: '#9CA3AF' },
];

const TYPE_ICONS = { boutique: <Heart size={14} />, strength: <Dumbbell size={14} />, endurance: <Flame size={14} /> };

function LeadCard({ lead, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [changing, setChanging] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [localActivity, setLocalActivity] = useState(lead.activity_log || []);
  const demo = lead.demo_account || {};
  const gymType = lead.gym_type || 'strength';
  const created = lead.created_at ? new Date(lead.created_at) : null;
  const daysAgo = created ? Math.floor((Date.now() - created.getTime()) / 86400000) : null;

  useEffect(() => { setLocalActivity(lead.activity_log || []); }, [lead.activity_log]);

  const changeStatus = async (newStatus) => {
    setChanging(true);
    try {
      await updateHuscleosLead(lead.id, { status: newStatus });
      onStatusChange();
      toast.success(`Moved to ${newStatus}`);
    } catch { toast.error('Failed to update'); }
    finally { setChanging(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const res = await addHuscleosNote(lead.id, noteText.trim());
      setLocalActivity(prev => [...prev, res.data.activity]);
      setNoteText('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setAddingNote(false); }
  };

  const copyToClip = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const sortedActivity = [...localActivity].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));

  return (
    <div
      className="bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all overflow-hidden"
      data-testid={`huscleos-lead-${lead.id}`}
    >
      {/* Header */}
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-gray-400 shrink-0">{TYPE_ICONS[gymType] || <Dumbbell size={14} />}</div>
            <h3 className="text-sm font-bold truncate" style={{ fontFamily: 'Manrope', color: '#0F0F0F', maxWidth: 180 }}>{lead.gym_name || 'Unknown Gym'}</h3>
          </div>
          {expanded ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
        </div>
        <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: 'Manrope' }}>{lead.contact_name}</p>
        <div className="flex items-center flex-wrap gap-1.5">
          {lead.demo_status === 'ready' && (
            <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 bg-gray-100 text-gray-700">Demo Ready</span>
          )}
          <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 bg-gray-100 text-gray-700">
            Drip {lead.drip_step || 0}/5
          </span>
          {localActivity.length > 0 && (
            <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 bg-gray-900 text-white">
              {localActivity.filter(a => a.action === 'note').length} notes
            </span>
          )}
          {daysAgo !== null && (
            <span className="text-[10px] text-gray-400 ml-auto">{daysAgo === 0 ? 'Today' : `${daysAgo}d ago`}</span>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-4">
          {/* Contact info */}
          <div className="space-y-1.5">
            {lead.email && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <Mail size={12} className="text-gray-400" /> <span className="truncate">{lead.email}</span>
                <button onClick={() => copyToClip(lead.email)} className="text-gray-400 hover:text-gray-900 ml-auto"><Copy size={10} /></button>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <Phone size={12} className="text-gray-400" /> <span>{lead.phone}</span>
                <button onClick={() => copyToClip(lead.phone)} className="text-gray-400 hover:text-gray-900 ml-auto"><Copy size={10} /></button>
              </div>
            )}
            {lead.city_state && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <MapPin size={12} className="text-gray-400" /> <span>{lead.city_state}</span>
              </div>
            )}
          </div>

          {/* Quiz data */}
          <div className="grid grid-cols-2 gap-2">
            {lead.member_count && (
              <div className="text-xs p-2 bg-gray-50 border border-gray-100">
                <span className="text-gray-500 block text-[10px] uppercase tracking-wider mb-0.5">Members</span>
                <span className="text-gray-900 font-medium">{lead.member_count}</span>
              </div>
            )}
            {lead.biggest_bottleneck && lead.biggest_bottleneck !== 'booking_call' && (
              <div className="text-xs p-2 bg-gray-50 border border-gray-100">
                <span className="text-gray-500 block text-[10px] uppercase tracking-wider mb-0.5">Bottleneck</span>
                <span className="text-gray-900 font-medium">{lead.biggest_bottleneck.replace(/_/g, ' ')}</span>
              </div>
            )}
          </div>

          {/* Demo account */}
          {demo.email && (
            <div className="p-3 bg-gray-900 space-y-1">
              <p className="text-[10px] font-bold tracking-widest uppercase text-[#FF2E00]">Demo Account</p>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-300 font-mono truncate">{demo.email}</p>
                <button onClick={() => copyToClip(`${demo.email} / ${demo.password}`)} className="text-gray-400 hover:text-white shrink-0 ml-2"><Copy size={12} /></button>
              </div>
              <p className="text-xs text-gray-500 font-mono">Pass: {demo.password}</p>
            </div>
          )}

          {/* Add Note */}
          <div data-testid={`note-input-${lead.id}`}>
            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">
              <MessageSquare size={10} className="inline mr-1" /> Add Note
            </p>
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                placeholder="Call notes, follow-up actions..."
                className="flex-1 px-3 py-2 text-xs bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                data-testid={`note-text-${lead.id}`}
                style={{ color: '#0F0F0F' }}
              />
              <button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}
                className="px-3 py-2 bg-gray-900 text-white hover:bg-black transition-colors disabled:opacity-30"
                data-testid={`note-submit-${lead.id}`}>
                <Send size={12} />
              </button>
            </div>
          </div>

          {/* Activity Timeline */}
          {sortedActivity.length > 0 && (
            <div data-testid={`activity-log-${lead.id}`}>
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Activity</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {sortedActivity.map((a, i) => {
                  const time = a.timestamp ? new Date(a.timestamp) : null;
                  const timeStr = time ? time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
                  return (
                    <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 border border-gray-100">
                      <div className="mt-0.5 shrink-0">
                        {a.action === 'note' ? <MessageSquare size={11} className="text-gray-900" /> :
                         a.action === 'status_change' ? <ArrowRightLeft size={11} className="text-[#FF2E00]" /> :
                         <Clock size={11} className="text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        {a.action === 'note' && (
                          <p className="text-xs text-gray-800 break-words">{a.note}</p>
                        )}
                        {a.action === 'status_change' && (
                          <p className="text-xs text-gray-700">
                            <span className="text-gray-400">{a.from_status}</span>
                            <span className="text-gray-400 mx-1">&rarr;</span>
                            <span className="font-bold" style={{ color: '#0F0F0F' }}>{a.to_status}</span>
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-gray-500">{a.staff_name || 'System'}</span>
                          <span className="text-[10px] text-gray-400">{timeStr}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status change */}
          <div>
            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Move to:</p>
            <div className="flex flex-wrap gap-1.5">
              {PIPELINE.filter(p => p.key !== lead.status).map(p => (
                <button key={p.key} onClick={() => changeStatus(p.key)} disabled={changing}
                  className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 border transition-all disabled:opacity-50 hover:bg-gray-900 hover:text-white hover:border-gray-900"
                  style={{ color: p.color, borderColor: '#E5E7EB' }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Recent Activity Feed — live tape of every team action across all prospects.
 * Pulls from POST /api/staff/dashboard/activity-feed. Refreshable, collapsible. */
function ActivityFeedWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/staff/dashboard/activity-feed?limit=15');
      setItems(r.data?.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mb-6 bg-white border border-gray-200 overflow-hidden" data-testid="activity-feed-widget">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="activity-feed-toggle">
        <div className="flex items-center gap-2">
          <MessageSquare size={14} className="text-gray-500" />
          <h2 className="text-[10px] font-bold tracking-widest uppercase text-gray-900">RECENT ACTIVITY</h2>
          <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-gray-900 text-white">{items.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span onClick={(e) => { e.stopPropagation(); load(); }} className="text-gray-400 hover:text-gray-900 cursor-pointer" data-testid="activity-feed-refresh"><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /></span>
          {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="max-h-72 overflow-y-auto" data-testid="activity-feed-list">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
          ) : items.length === 0 ? (
            <div className="px-5 py-8 text-center text-xs text-gray-500">No team activity yet. Once you and your teammates start working prospects, every action shows up here in real time.</div>
          ) : (
            items.map((it, i) => {
              const when = it.timestamp ? new Date(it.timestamp) : null;
              const minsAgo = when ? Math.max(0, Math.round((Date.now() - when.getTime()) / 60000)) : null;
              const ago = minsAgo == null ? '—' : minsAgo < 1 ? 'just now' : minsAgo < 60 ? `${minsAgo}m ago` : minsAgo < 1440 ? `${Math.round(minsAgo / 60)}h ago` : `${Math.round(minsAgo / 1440)}d ago`;
              const action = (it.action || 'note').replace(/_/g, ' ');
              const isHotAction = ['demo_page_viewed', 'demo_email_captured'].includes(it.action);
              return (
                <div key={i} className="flex items-start gap-3 px-5 py-2.5 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors" data-testid={`activity-${i}`}>
                  <div className={`shrink-0 w-6 h-6 flex items-center justify-center mt-0.5 ${isHotAction ? 'bg-[#FF2E00]' : 'bg-gray-900'}`}>
                    <span className="text-white text-[10px] font-bold uppercase">{(it.staff_name || '?').slice(0, 1).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-900 leading-snug">
                      <span className="font-bold">{it.staff_name || 'system'}</span>
                      <span className="text-gray-500"> · {action} · </span>
                      <span className="text-gray-700 font-semibold">{it.gym_name}</span>
                    </p>
                    {it.note && <p className="text-[11px] text-gray-500 leading-snug mt-0.5 truncate" title={it.note}>{it.note}</p>}
                  </div>
                  <span className="text-[10px] text-gray-400 shrink-0 tabular-nums whitespace-nowrap">{ago}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}


/** Hot Demos This Week — surfaces prospects whose public /demo/[slug] page got hit recently.
 * Closes the loop between "demo viewed" and "staff reaches out" (proven 9x conversion lift). */
function HotDemosWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/staff/dashboard/hot-demos?days=${days}&limit=10`);
      setItems(r.data?.items || []);
    } catch { /* silent — empty state handles it */ }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && items.length === 0) {
    return (
      <div className="mb-6 p-5 bg-white border border-gray-200" data-testid="hot-demos-widget-loading">
        <Loader2 size={18} className="animate-spin text-gray-400 mx-auto" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-white border border-orange-200" data-testid="hot-demos-widget-empty">
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <Flame size={16} className="text-[#FF2E00]" />
          <span><strong className="text-gray-900">No demo views this week.</strong> Publish a public demo page from any prospect — they get a personalized landing page you can drop into cold outreach.</span>
          <Link to="/staff/prospects" className="ml-auto text-xs font-bold text-[#FF2E00] hover:underline" data-testid="hot-demos-go-prospects">Open Prospects →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white border border-gray-200 overflow-hidden" data-testid="hot-demos-widget">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-white">
        <div className="flex items-center gap-2">
          <Flame size={14} className="text-[#FF2E00]" />
          <h2 className="text-[10px] font-bold tracking-widest uppercase text-gray-900">HOT DEMOS · LAST {days}D</h2>
          <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-[#FF2E00] text-white" data-testid="hot-demos-count">{items.length}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 transition-colors ${days === d ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}
              data-testid={`hot-demos-range-${d}`}>{d}D</button>
          ))}
          <button onClick={load} className="text-gray-400 hover:text-gray-900 ml-1" data-testid="hot-demos-refresh"><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-gray-500 border-b border-gray-100 bg-gray-50">
            <th className="text-left py-2 px-4 font-bold tracking-widest uppercase">GYM</th>
            <th className="text-left py-2 px-3 font-bold tracking-widest uppercase hidden sm:table-cell">LOCATION</th>
            <th className="text-center py-2 px-3 font-bold tracking-widest uppercase">VIEWS</th>
            <th className="text-center py-2 px-3 font-bold tracking-widest uppercase">EMAILS</th>
            <th className="text-left py-2 px-3 font-bold tracking-widest uppercase hidden md:table-cell">LAST SEEN</th>
            <th className="text-right py-2 px-4 font-bold tracking-widest uppercase">DEMO</th>
          </tr></thead>
          <tbody>
            {items.map((p, idx) => {
              const lastSeen = p.demo_last_viewed_at ? new Date(p.demo_last_viewed_at) : null;
              const minutesAgo = lastSeen ? Math.round((Date.now() - lastSeen.getTime()) / 60000) : null;
              const ago = minutesAgo == null ? '—' : minutesAgo < 60 ? `${minutesAgo}m ago` : minutesAgo < 1440 ? `${Math.round(minutesAgo / 60)}h ago` : `${Math.round(minutesAgo / 1440)}d ago`;
              const slug = p.demo_slug || p.demo_token;
              const isHotAF = minutesAgo != null && minutesAgo < 60;
              return (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-orange-50/30 transition-colors" data-testid={`hot-demo-row-${idx}`}>
                  <td className="py-2.5 px-4">
                    <p className="font-bold text-gray-900 leading-tight">{p.gym_name}</p>
                  </td>
                  <td className="py-2.5 px-3 text-gray-500 hidden sm:table-cell">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="py-2.5 px-3 text-center">
                    <span className="font-bold tabular-nums text-gray-900 inline-flex items-center gap-1"><Eye size={11} className="text-gray-400" />{p.demo_views}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {p.captures_count > 0 ? (
                      <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">{p.captures_count} CAPTURED</span>
                    ) : (
                      <span className="text-gray-300 tabular-nums">0</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 hidden md:table-cell">
                    {isHotAF ? (
                      <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-[#FF2E00] text-white">🔥 {ago}</span>
                    ) : (
                      <span className="text-gray-500">{ago}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <a href={`/demo/${slug}`} target="_blank" rel="noreferrer" className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 border border-gray-200 hover:border-gray-900 text-gray-700 inline-flex items-center gap-1" data-testid={`hot-demo-open-${idx}`}>
                      <ExternalLink size={10} /> OPEN
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-[10px] text-gray-500">Demo viewed in last {days}d → strike while warm. Top of funnel signal.</p>
      </div>
    </div>
  );
}


export default function HuscleOSDashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, sr] = await Promise.all([getHuscleosLeads(), getHuscleosStats()]);
      setLeads(lr.data.leads || []);
      setStats(sr.data || {});
    } catch (err) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Bucket leads into pipeline columns
  const bucketed = {};
  PIPELINE.forEach(p => { bucketed[p.key] = []; });
  leads.forEach(l => {
    let status = l.status || 'New';
    if (status === 'New' && l.demo_status === 'ready') status = 'Demo Sent';
    if (!bucketed[status]) status = 'New';
    bucketed[status].push(l);
  });

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
              <span className="text-xs font-bold tracking-widest uppercase text-gray-400 ml-2 hidden sm:inline">SALES</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} disabled={loading} className="p-2 text-gray-500 hover:text-gray-900 transition-colors" data-testid="huscleos-refresh" aria-label="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <Link to="/huscleos" target="_blank" className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-900 transition-colors">
              <ExternalLink size={14} /> Landing
            </Link>
            <Link to="/staff/onboarding" className="hidden sm:flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-1.5 bg-gray-900 text-white hover:bg-black transition-colors" data-testid="nav-onboarding">
              Onboarding
            </Link>
            <Link to="/staff/prospects" className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-1.5 border-2 hover:bg-[#FF2E00] hover:text-white hover:border-[#FF2E00] transition-colors" style={{ borderColor: '#FF2E00', color: '#FF2E00' }} data-testid="nav-prospects">
              Prospects
            </Link>
            <Link to="/staff/merch" className="hidden md:flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors" data-testid="nav-merch">
              Merch
            </Link>
            <Link to="/staff/team" className="hidden md:flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors" data-testid="nav-team">
              <Users size={12} /> Team
            </Link>
            <Link to="/staff/settings" className="hidden md:flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors" data-testid="nav-settings" title="Branding + Public API keys">
              Settings
            </Link>
            <DemoSwitcher />
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* MRR + Funnel Stats (live, refreshes every 30s) */}
        <MRRStatsBar />

        {/* Live team activity tape */}
        <ActivityFeedWidget />

        {/* Hot Demos This Week — pulled from public demo view counters */}
        <HotDemosWidget />

        {loading && leads.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : (
          /* Kanban Pipeline */
          <div className="overflow-x-auto -mx-4 px-4 pb-4">
            <div className="flex gap-4" style={{ minWidth: PIPELINE.length * 280 }} data-testid="huscleos-pipeline">
              {PIPELINE.map(col => (
                <div key={col.key} className="flex-1 min-w-[260px]">
                  {/* Column header */}
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: col.dot }} />
                    <h2 className="text-xs font-bold tracking-widest uppercase text-gray-600">{col.label}</h2>
                    <span className="ml-auto text-[11px] font-bold px-2 py-0.5 tabular-nums" style={{ background: col.chipBg, color: col.chipFg }}>
                      {bucketed[col.key]?.length || 0}
                    </span>
                  </div>
                  {/* Cards */}
                  <div className="space-y-2">
                    {(bucketed[col.key] || []).map(lead => (
                      <LeadCard key={lead.id} lead={lead} onStatusChange={load} />
                    ))}
                    {(bucketed[col.key] || []).length === 0 && (
                      <div className="p-6 border border-dashed border-gray-200 text-center">
                        <p className="text-xs text-gray-400">No leads</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
